import {ToggleCoinsResponse_EnabledHint} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {Fade, FadeTransition, Grow} from '@aboutyou/transitions';
import {
  COLORS,
  GenericIconName,
  Icon,
  IconSize,
  ProductImage,
  Text,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import coinsWalletBackground from '../../../features/Coins/assets/background-dark.png';
import {RedeemCoinsCard} from './RedeemCoinsCard';
import {RedeemCoinsError} from './RedeemCoinsError';
import {RedeemCoinsStep} from './hooks/useRedeemCoins';

const MAX_PRODUCT_IMAGES = 4;

// Timings from the app animation: slide left 500-800ms, fanout 700-1150ms
// (both covered by the "unfold" keyframes).
const UNFOLD_ANIMATION_DELAY = 500;
const UNFOLD_ANIMATION_DURATION = 650;

export interface RedeemCoinsPopupContentProps {
  data: {
    coins: string;
    equivalentAmount: string;
    enabledHint?: ToggleCoinsResponse_EnabledHint;
  };
  step?: RedeemCoinsStep;
}

export const RedeemCoinsPopupContent: React.FC<
  RedeemCoinsPopupContentProps
> = ({data, step}) => {
  const {t} = useTranslation();

  const productImages = data.enabledHint?.productImages;

  if (step === RedeemCoinsStep.Error) {
    return (
      <Fade mounted={true} duration={500}>
        <RedeemCoinsError />
      </Fade>
    );
  }

  const shownProductImages =
    productImages && productImages.slice(0, MAX_PRODUCT_IMAGES);

  return (
    <Wrapper data-step={step}>
      <FadeTransition duration={200} uniqueKey={step?.toString()}>
        <Text
          size="px20"
          weight="w500"
          id="focusableText"
          data-testid="redeemCoinsPopupContentTitle"
        >
          {step === RedeemCoinsStep.Applied
            ? data.enabledHint?.title
            : t('Just a second, your discount is being prepared!')}
        </Text>
      </FadeTransition>
      <Subheading size="px12" color="Grey0">
        {t('Unwrapping your discount.')}
      </Subheading>
      <CardRow>
        <CoinsCard
          icon={GenericIconName.CoinsDoubleColor}
          title={data.coins}
          subtitle={t('Coins')}
        />
        <div>
          <RightArrow
            asset={GenericIconName.LongArrow}
            size="S"
            fill={COLORS['Fill.Grey.Inactive']}
          />
          <LeftArrow
            asset={GenericIconName.LongArrow}
            size="S"
            fill={COLORS['Fill.Grey.Secondary']}
          />
        </div>
        <ValueCard
          icon={GenericIconName.BasketLock}
          title={data.equivalentAmount}
          subtitle={t('Discount')}
        >
          <IconWrapper>
            <Grow mounted={step === RedeemCoinsStep.Preparing} duration={200}>
              <SuccessIcon
                asset={GenericIconName.CheckmarkCircleWhiteCheck}
                fill={COLORS['Alerts.Success.Primary']}
              />
            </Grow>
          </IconWrapper>
        </ValueCard>
      </CardRow>
      {shownProductImages && (
        <ProductImages
          $itemsCount={
            productImages.length > MAX_PRODUCT_IMAGES
              ? MAX_PRODUCT_IMAGES + 1
              : productImages.length
          }
        >
          {shownProductImages.map((image, index) => (
            <ProductImageWrapper key={index} $index={index}>
              <ProductImage src={image.src} padded widths={[100]} />
              <SuccessCoinsIconWrapper>
                <Icon
                  asset={GenericIconName.CoinsDoubleColorSuccess}
                  size="XS"
                  fill={COLORS.White}
                />
              </SuccessCoinsIconWrapper>
            </ProductImageWrapper>
          ))}
          {productImages.length > MAX_PRODUCT_IMAGES && (
            <MoreProducts>
              + {productImages.length - MAX_PRODUCT_IMAGES}
            </MoreProducts>
          )}
        </ProductImages>
      )}
      <EnabledHint size="px10">{data.enabledHint?.hint}</EnabledHint>
    </Wrapper>
  );
};
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
`;

const Subheading = styled(Text)`
  margin-top: 24px;
  transition: opacity 0.3s ease-in-out;
  opacity: 0;

  [data-step='${RedeemCoinsStep.Loading}'] & {
    opacity: 1;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  right: -10px;
  top: -10px;
`;

const SuccessIcon = styled(Icon)`
  width: 32px;
  height: 32px;
  flex-basis: 32px;
`;

const arrowStyles = `
  transition: opacity 0.1s ease-in-out;
  opacity: 0;
  &&& { height: 8px; }

  [data-step='${RedeemCoinsStep.Loading}'] & {
    animation: flicker 0.4s infinite;
    opacity: 1;

    @keyframes flicker {
      from {
        fill: ${COLORS['Fill.Grey.Secondary']};
      }
      to {
        fill: ${COLORS['Fill.Grey.Inactive']};
      }
    }
  }
`;

const LeftArrow = styled(Icon)`
  ${arrowStyles};
  transform: rotate(180deg);

  &&& {
    animation-delay: 0.2s;
  }
`;

const RightArrow = styled(Icon)`
  ${arrowStyles};
`;

const CardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 24px;
`;

const CoinsCard = styled(RedeemCoinsCard)`
  background-color: ${COLORS['Fill.Grey.Primary']};
  transition: transform 0.25s ease-in-out;

  [data-step='${RedeemCoinsStep.Preparing}'] & {
    // travels half the width of the card + width of the arrow
    transform: translate(calc(50% + ${IconSize.S}px), -40px);
  }

  [data-step='${RedeemCoinsStep.Applied}'] & {
    opacity: 0;
  }
`;

const ValueCard = styled(RedeemCoinsCard)`
  background-image: url(${coinsWalletBackground});
  background-size: 100%;
  transition: transform 0.2s ease-in-out, opacity 0.1s ease-in-out;

  span {
    color: ${COLORS.White};
  }

  [data-step='${RedeemCoinsStep.Preparing}'] & {
    // travels half of the width + width of the arrow minus the grid gap
    transform: translate(calc(-1 * (50% + ${IconSize.S}px - 0.5rem)), -40px);
  }

  [data-step='${RedeemCoinsStep.Applied}'] & {
    transform: translate(-75%, -40px);
    opacity: 0;
  }

  z-index: ${MAX_PRODUCT_IMAGES};
`;

const ProductImages = styled.div<{$itemsCount: number}>`
  --image-width: 58px;
  --image-gap: 8px;
  --container-width: ${props => `calc(
    var(--image-width) * ${props.$itemsCount} + var(--image-gap) *
      ${props.$itemsCount - 1}
  )`};

  position: relative;
  justify-content: center;
  margin: 0 auto;
  height: 80px;
  width: var(--container-width);

  transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out;
  transform: translateY(-80px);
  opacity: 0;

  [data-step='${RedeemCoinsStep.Applied}'] & {
    transform: translateY(-120px);
    opacity: 1;
  }
`;

const productImagesItemsStyles = `
  position: absolute;
  width: var(--image-width);
  height: 100%;
  border-radius: 4px;

  /* stacked in the center of the container */
  transform: translateX(
    calc((var(--container-width) - var(--image-width)) / 2)
  );

  @keyframes unfold {
    40% {
      transform: translateX(0);
    }
    100% {
      transform: var(--unfold-transform);
    }
  }
`;

const ProductImageWrapper = styled.div<{
  $index: number;
}>`
  ${productImagesItemsStyles};
  padding: 0;
  z-index: ${props => MAX_PRODUCT_IMAGES - props.$index};

  > img {
    padding: 8px;
  }

  [data-step='${RedeemCoinsStep.Applied}'] & {
    animation: unfold ${UNFOLD_ANIMATION_DURATION}ms ease-in-out forwards;
    animation-delay: ${UNFOLD_ANIMATION_DELAY}ms;
    --unfold-transform: ${({$index}) =>
      `translateX(calc(${$index} * (var(--image-width) + var(--image-gap))))`};
  }
`;

const SuccessCoinsIconWrapper = styled.div`
  width: 24px;
  height: 24px;
  position: absolute;
  left: -6px;
  top: -6px;
  padding: 4px;
  background-color: ${COLORS.White};
  border-radius: 100%;
  filter: drop-shadow(0px 2px 10px rgba(0, 0, 0, 0.15));
`;

const MoreProducts = styled.div`
  ${productImagesItemsStyles};
  background-color: ${COLORS['Fill.Grey.Primary']};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;
  color: ${COLORS['Text.Grey']};

  [data-step='${RedeemCoinsStep.Applied}'] & {
    animation: unfold ${UNFOLD_ANIMATION_DURATION}ms ease-in-out forwards;
    animation-delay: ${UNFOLD_ANIMATION_DELAY}ms;
    --unfold-transform: translateX(
      calc(${MAX_PRODUCT_IMAGES} * (var(--image-width) + var(--image-gap)))
    );
  }
`;

const EnabledHint = styled(Text)`
  margin-top: 8px;
  color: ${COLORS['Text.Grey']};
  transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out;
  transform: translateY(-80px);
  opacity: 0;

  [data-step='${RedeemCoinsStep.Applied}'] & {
    opacity: 1;
    transform: translateY(-116px);
  }
`;
